import React, { useState, useEffect, useMemo } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { OrderRequestTab } from './order-request-tab/order-request-tab';
import { TabTitle } from './components/tab-title';
import './order-approval-tabs.css';
import { OrderOrganizeTab } from './order-organize-tab/order-organize-tab';
import { OrderPackingTab } from './order-packing-tab/order-packing-tab';
import { OrderCollectorTab } from './order-collector-tab/order-collector-tab';
import { useFormikContext } from 'formik';
import {
  useTabVisibleByStatus,
  useDefaultTabKeyForStatus,
  APPROVAL_TAB_KEYS,
  type ApprovalTabKey,
} from './services/tab-visible-by-status';
import { isPackagingStepNeeded } from './services/is-packaging-step-needed';
import { ActionsHistoryTabContent } from './actions-history-tab/actions-history-tab-content';

const TAB_KEYS_ORDER: (keyof typeof APPROVAL_TAB_KEYS)[] = [
  'Request',
  'Organize',
  'Packing',
  'Collector',
];
export const OrderApprovalTabs = ({ data, isNew, refetch }) => {
  const xrbData = data?.xrbData;
  const packagingStepIsNeeded = isPackagingStepNeeded(xrbData);

  const historyData = data?.historyData;
  const tabVisible = useTabVisibleByStatus();
  const defaultTabForStatus = useDefaultTabKeyForStatus();
  const visibleTabKeys = useMemo(
    () => TAB_KEYS_ORDER.map((k) => APPROVAL_TAB_KEYS[k]).filter((key) => tabVisible[key]),
    [tabVisible],
  );
  const firstVisibleTab = visibleTabKeys[0] ?? APPROVAL_TAB_KEYS.Request;

  const [key, setKey] = useState<ApprovalTabKey>(defaultTabForStatus);

  // When status (or packagingStepIsNeeded) changes, switch to the default tab for that status
  useEffect(() => {
    setKey(defaultTabForStatus);
  }, [defaultTabForStatus]);

  useEffect(() => {
    if (key !== APPROVAL_TAB_KEYS.ActionHistory && firstVisibleTab && !tabVisible[key]) {
      setKey(firstVisibleTab);
    }
  }, [firstVisibleTab, key, tabVisible]);

  return (
    <Tabs
      className="order-approval-tabs"
      activeKey={key}
      onSelect={(k) => k != null && setKey(k as ApprovalTabKey)}
    >
      {tabVisible[APPROVAL_TAB_KEYS.Request] && (
        <Tab
          eventKey={APPROVAL_TAB_KEYS.Request}
          title={<TabTitle label="Αίτημα" badge="1" active={key === APPROVAL_TAB_KEYS.Request} />}
        >
          {key === APPROVAL_TAB_KEYS.Request && <OrderRequestTab data={data} xrbData={xrbData} />}
        </Tab>
      )}

      {tabVisible[APPROVAL_TAB_KEYS.Organize] && (
        <Tab
          eventKey={APPROVAL_TAB_KEYS.Organize}
          title={
            <TabTitle label="Οργάνωση" badge="2" active={key === APPROVAL_TAB_KEYS.Organize} />
          }
        >
          {key === APPROVAL_TAB_KEYS.Organize && <OrderOrganizeTab data={data} refetch={refetch} />}
        </Tab>
      )}

      {tabVisible[APPROVAL_TAB_KEYS.Packing] && (
        <Tab
          eventKey={APPROVAL_TAB_KEYS.Packing}
          title={
            <TabTitle label="Συσκευασία" badge={'3'} active={key === APPROVAL_TAB_KEYS.Packing} />
          }
        >
          {key === APPROVAL_TAB_KEYS.Packing && <OrderPackingTab data={data} refetch={refetch} />}
        </Tab>
      )}

      {tabVisible[APPROVAL_TAB_KEYS.Collector] && (
        <Tab
          eventKey={APPROVAL_TAB_KEYS.Collector}
          title={
            <TabTitle
              label="Συλλογή"
              badge={packagingStepIsNeeded ? '4' : '3'}
              active={key === APPROVAL_TAB_KEYS.Collector}
            />
          }
        >
          {key === APPROVAL_TAB_KEYS.Collector && <OrderCollectorTab data={data} />}
        </Tab>
      )}
      {tabVisible[APPROVAL_TAB_KEYS.ActionHistory] && (
        <Tab
          eventKey={APPROVAL_TAB_KEYS.ActionHistory}
          title={
            <TabTitle
              label="Ιστορικό Ενεργειών"
              active={key === APPROVAL_TAB_KEYS.ActionHistory}
              systemTab
            />
          }
        >
          {key === APPROVAL_TAB_KEYS.ActionHistory && (
            <ActionsHistoryTabContent collectionOrderId={data?.id} />
          )}
        </Tab>
      )}
    </Tabs>
  );
};
